import {
  loadFixture, paginate, findById, nextId, parseBody, parseQuery, urlParam,
} from '../mockDataStore';

const PROMPT_DETAIL = /^\/?api\/v1\/groups\/[\w-]+\/prompts\/(\d+)\/?$/;
const VIDEO_DETAIL = /^\/?api\/v1\/groups\/[\w-]+\/videos\/(\d+)\/?$/;

export default function registerVideoMocks(mock) {
  mock.onGet(/^\/?api\/v1\/groups\/[\w-]+\/prompts\/?(\?.*)?$/).reply(async (config) => {
    const prompts = await loadFixture('video/prompts');
    return [200, paginate(prompts.results, parseQuery(config.url))];
  });

  mock.onPost(/^\/?api\/v1\/groups\/[\w-]+\/prompts\/?$/).reply(async (config) => {
    const prompts = await loadFixture('video/prompts');
    const created = { id: nextId(prompts.results), is_visible: true, ...parseBody(config) };
    prompts.results.push(created);
    prompts.count += 1;
    return [201, created];
  });

  mock.onPatch(PROMPT_DETAIL).reply(async (config) => {
    const prompts = await loadFixture('video/prompts');
    const prompt = findById(prompts.results, Number(urlParam(config, PROMPT_DETAIL)));
    if (!prompt) return [404, { message: 'Not found.' }];
    Object.assign(prompt, parseBody(config));
    return [200, prompt];
  });

  mock.onPost(/^\/?api\/v1\/groups\/[\w-]+\/videos\/?$/).reply(async (config) => {
    const videos = await loadFixture('video/videos');
    const body = parseBody(config);
    const created = {
      id: nextId(videos.results),
      task_id: `demo-task-${Date.now()}`,
      s3_key: body.s3_key,
      prompt_id: body.prompt_id ? Number(body.prompt_id) : null,
      // `uploadVideo` sends this via FormData, so it arrives as the string "true"/"false"
      // rather than a boolean.
      is_visible: String(body.is_visible) !== 'false',
    };
    videos.results.push(created);
    videos.count += 1;
    return [201, created];
  });

  mock.onGet(VIDEO_DETAIL).reply(async (config) => {
    const videos = await loadFixture('video/videos');
    const video = findById(videos.results, Number(urlParam(config, VIDEO_DETAIL)));
    return video ? [200, video] : [404, { message: 'Not found.' }];
  });

  mock.onPatch(VIDEO_DETAIL).reply(async (config) => {
    const videos = await loadFixture('video/videos');
    const video = findById(videos.results, Number(urlParam(config, VIDEO_DETAIL)));
    if (!video) return [404, { message: 'Not found.' }];
    Object.assign(video, parseBody(config));
    return [200, video];
  });

  mock.onDelete(VIDEO_DETAIL).reply(async (config) => {
    const videos = await loadFixture('video/videos');
    const id = Number(urlParam(config, VIDEO_DETAIL));
    videos.results = videos.results.filter((video) => video.id !== id);
    videos.count = videos.results.length;
    return [200, {}];
  });

  // Real endpoint returns S3 POST-policy fields for a direct browser->S3 upload. The
  // upload itself is short-circuited in demo mode — see the isDemoEnvironment() branch
  // added to `uploadToS3` in src/store/modules/video.js (plan §8) — so these values are
  // never actually used to hit S3; they only need to satisfy the response shape.
  mock.onGet(/^\/?api\/v1\/videos\/sign_s3_url\/?(\?.*)?$/).reply(() => [
    200,
    {
      url: 'https://demo.invalid/s3-upload',
      fields: {
        key: `demo-uploads/${Date.now()}.mp4`,
        a_w_s_access_key_id: 'demo-access-key-id',
        policy: 'demo-policy',
        signature: 'demo-signature',
      },
    },
  ]);

  // Instant success. Plan §12 leaves faking a pending->success polling delay as an open
  // question; this picks the simpler behavior until stakeholders decide otherwise.
  mock.onGet(/^\/?api\/v1\/tasks\/[\w-]+\/?$/).reply(() => [
    200,
    { success: true, status: 'complete', result: null },
  ]);
}
